import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Brain, TrendingUp, AlertTriangle, Clock, MapPin, Activity, Loader2,
  MessageCircle, Share2, Siren, Navigation, CalendarPlus, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kalaburagi areas for prediction
const AREAS = [
  'Jagat Circle', 'Super Market Area', 'University Road', 'Ring Road',
  'Humnabad Cross', 'Station Road', 'Aland Road', 'Sedam Road',
  'Jewargi Colony', 'MSK Mill Area', 'Govt Hospital Road', 'Bus Stand Area'
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i % 12 || 12;
  const ampm = i < 12 ? 'AM' : 'PM';
  return { value: i, label: `${h}:00 ${ampm}` };
});

export default function SmartFeaturesPage() {
  const { authAxios, user } = useAuth();
  const [activeTab, setActiveTab] = useState('prediction');

  // AI Prediction state
  const [predArea, setPredArea] = useState('');
  const [predHour, setPredHour] = useState('');
  const [predDay, setPredDay] = useState('');
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);

  // Emergency state
  const [emergencyType, setEmergencyType] = useState('');
  const [emergencyResult, setEmergencyResult] = useState(null);

  // Events state
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ name: '', area: '', date: '', description: '', impact: 'medium' });
  const [addingEvent, setAddingEvent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  // AI Congestion Prediction
  const handlePredict = async () => {
    if (!predArea || predHour === '' || !predDay) {
      toast.error('Select area, day, and time');
      return;
    }
    setPredicting(true);
    setPrediction(null);
    try {
      const res = await (user ? authAxios : axios).post(`${API}/ai/predict-congestion`, {
        area: predArea,
        hour: parseInt(predHour),
        day: predDay
      });
      setPrediction(res.data);
    } catch (err) {
      toast.error('Prediction failed. Try again.');
    }
    setPredicting(false);
  };

  // Emergency Routes
  const EMERGENCY_SERVICES = {
    hospital: [
      { name: 'Govt District Hospital', lat: 17.3310, lng: 76.8340, phone: '08472-278800' },
      { name: 'ESIC Hospital', lat: 17.3350, lng: 76.8290, phone: '08472-256700' },
      { name: 'Basaveshwar Hospital', lat: 17.3270, lng: 76.8380, phone: '08472-255100' },
    ],
    police: [
      { name: 'City Police Station', lat: 17.3300, lng: 76.8330, phone: '08472-255100' },
      { name: 'Traffic Police Station', lat: 17.3280, lng: 76.8350, phone: '103' },
      { name: 'SP Office', lat: 17.3320, lng: 76.8300, phone: '08472-255200' },
    ],
    fire: [
      { name: 'Fire Station Kalaburagi', lat: 17.3290, lng: 76.8310, phone: '101' },
    ]
  };

  const handleEmergency = (type) => {
    setEmergencyType(type);
    setEmergencyResult(EMERGENCY_SERVICES[type] || []);
  };

  // WhatsApp share
  const shareOnWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // Events
  const addEvent = async () => {
    if (!newEvent.name || !newEvent.area || !newEvent.date) {
      toast.error('Fill all required fields');
      return;
    }
    setAddingEvent(true);
    try {
      const res = await authAxios.post(`${API}/events`, newEvent);
      setEvents(prev => [res.data, ...prev]);
      setNewEvent({ name: '', area: '', date: '', description: '', impact: 'medium' });
      toast.success('Event added!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add event');
    }
    setAddingEvent(false);
  };

  const deleteEvent = async (eventId) => {
    try {
      await authAxios.delete(`${API}/events/${eventId}`);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const tabs = [
    { id: 'prediction', label: 'AI Prediction', icon: Brain },
    { id: 'emergency', label: 'Emergency', icon: Siren },
    { id: 'whatsapp', label: 'WhatsApp Alerts', icon: MessageCircle },
    { id: 'events', label: 'Event Planning', icon: CalendarPlus },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="smart-features-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Brain className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Smart Features</h1>
          <p className="text-sm text-muted-foreground">AI predictions, emergency routes, and more</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="smart-tabs">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            size="sm"
            className="gap-2 flex-shrink-0"
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />{tab.label}
          </Button>
        ))}
      </div>

      {/* AI Congestion Prediction */}
      {activeTab === 'prediction' && (
        <div className="space-y-6 animate-fade-in" data-testid="prediction-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary" />
                AI Congestion Prediction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Our AI analyzes historical traffic data and reports to predict congestion levels for any area and time.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Area</Label>
                  <Select value={predArea} onValueChange={setPredArea}>
                    <SelectTrigger data-testid="pred-area-select">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Day</Label>
                  <Select value={predDay} onValueChange={setPredDay}>
                    <SelectTrigger data-testid="pred-day-select">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Select value={predHour} onValueChange={setPredHour}>
                    <SelectTrigger data-testid="pred-hour-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => <SelectItem key={h.value} value={String(h.value)}>{h.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handlePredict} disabled={predicting} className="gap-2" data-testid="predict-btn">
                {predicting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                {predicting ? 'Analyzing...' : 'Predict Congestion'}
              </Button>

              {prediction && (
                <div className="mt-6 p-6 rounded-xl border animate-fade-in-up" data-testid="prediction-result">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">{prediction.area}</h3>
                    <Badge className={`text-sm ${
                      prediction.level === 'High' ? 'bg-red-500' :
                      prediction.level === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                    } text-white`}>
                      {prediction.level} Congestion
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-muted">
                      <span className="text-xs text-muted-foreground block">Congestion Score</span>
                      <span className="text-2xl font-bold">{prediction.score}/10</span>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <span className="text-xs text-muted-foreground block">Est. Delay</span>
                      <span className="text-2xl font-bold">{prediction.delay_minutes} min</span>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <span className="text-xs text-muted-foreground block">Best Alt. Route</span>
                      <span className="text-sm font-medium">{prediction.alt_route}</span>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <span className="text-xs text-muted-foreground block">Peak Clears By</span>
                      <span className="text-sm font-medium">{prediction.clears_by}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{prediction.advice}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 gap-2"
                    onClick={() => shareOnWhatsApp(
                      `🚦 Kalaburagi Traffic Alert\n${prediction.area}: ${prediction.level} congestion expected on ${predDay} at ${HOURS[parseInt(predHour)]?.label}\nDelay: ~${prediction.delay_minutes} min\nAlt route: ${prediction.alt_route}\n\nCheck: ${window.location.origin}/smart`
                    )}
                    data-testid="share-prediction-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" /> Share on WhatsApp
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Emergency Routes */}
      {activeTab === 'emergency' && (
        <div className="space-y-6 animate-fade-in" data-testid="emergency-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Siren className="w-5 h-5 text-accent" />
                Emergency Route Finder
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Find the nearest emergency services in Kalaburagi with one tap.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Button
                  variant={emergencyType === 'hospital' ? 'default' : 'outline'}
                  className="gap-2 h-auto py-4 flex-col"
                  onClick={() => handleEmergency('hospital')}
                  data-testid="emergency-hospital"
                >
                  <span className="text-2xl">🏥</span>
                  <span className="font-bold">Hospital</span>
                  <span className="text-xs opacity-70">Ambulance: 108</span>
                </Button>
                <Button
                  variant={emergencyType === 'police' ? 'default' : 'outline'}
                  className="gap-2 h-auto py-4 flex-col"
                  onClick={() => handleEmergency('police')}
                  data-testid="emergency-police"
                >
                  <span className="text-2xl">👮</span>
                  <span className="font-bold">Police</span>
                  <span className="text-xs opacity-70">Helpline: 100</span>
                </Button>
                <Button
                  variant={emergencyType === 'fire' ? 'default' : 'outline'}
                  className="gap-2 h-auto py-4 flex-col"
                  onClick={() => handleEmergency('fire')}
                  data-testid="emergency-fire"
                >
                  <span className="text-2xl">🚒</span>
                  <span className="font-bold">Fire</span>
                  <span className="text-xs opacity-70">Fire: 101</span>
                </Button>
              </div>

              {emergencyResult && (
                <div className="space-y-3 animate-fade-in-up" data-testid="emergency-results">
                  {emergencyResult.map((place, i) => (
                    <div key={i} className="p-4 rounded-xl border flex items-center justify-between gap-4">
                      <div>
                        <h4 className="font-bold text-sm">{place.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {place.lat.toFixed(4)}, {place.lng.toFixed(4)}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <a href={`tel:${place.phone}`}>
                          <Button size="sm" variant="destructive" className="gap-1" data-testid={`call-${i}`}>
                            <Siren className="w-3 h-3" /> Call
                          </Button>
                        </a>
                        <a
                          href={`https://www.openstreetmap.org/directions?from=&to=${place.lat},${place.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline" className="gap-1" data-testid={`navigate-${i}`}>
                            <Navigation className="w-3 h-3" /> Navigate
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* WhatsApp Alerts */}
      {activeTab === 'whatsapp' && (
        <div className="space-y-6 animate-fade-in" data-testid="whatsapp-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                WhatsApp Traffic Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Share traffic updates instantly with your contacts and community groups on WhatsApp.
              </p>

              {/* Quick Alert Templates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: 'Congestion Alert', msg: '🚦 Heavy traffic at Jagat Circle, Kalaburagi. Expect 15-20 min delay. Use Ring Road as alternative.\n\nReport via: ' + window.location.origin },
                  { title: 'Road Block Alert', msg: '🚧 Road blocked near Station Road, Kalaburagi due to repair work. Diversion via Super Market Road.\n\nReport via: ' + window.location.origin },
                  { title: 'Accident Alert', msg: '⚠️ Accident reported near University Road, Kalaburagi. Traffic moving slowly. Drive carefully.\n\nReport via: ' + window.location.origin },
                  { title: 'Signal Down', msg: '🚦 Traffic signal not working at Humnabad Cross, Kalaburagi. Police managing traffic manually.\n\nReport via: ' + window.location.origin },
                ].map((template, i) => (
                  <div key={i} className="p-4 rounded-xl border hover:shadow-md transition-shadow">
                    <h4 className="font-bold text-sm mb-2">{template.title}</h4>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{template.msg}</p>
                    <Button
                      size="sm"
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      onClick={() => shareOnWhatsApp(template.msg)}
                      data-testid={`whatsapp-template-${i}`}
                    >
                      <MessageCircle className="w-3 h-3" /> Share on WhatsApp
                    </Button>
                  </div>
                ))}
              </div>

              {/* Custom Alert */}
              <div className="p-4 rounded-xl border bg-muted/30">
                <h4 className="font-bold text-sm mb-3">Custom Traffic Alert</h4>
                <Textarea
                  id="custom-alert"
                  placeholder="Type your custom traffic alert message..."
                  className="mb-3"
                  rows={3}
                  data-testid="custom-alert-input"
                />
                <Button
                  size="sm"
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const msg = document.getElementById('custom-alert').value;
                    if (msg) shareOnWhatsApp(`🚦 Kalaburagi Traffic Update:\n${msg}\n\nvia Kalaburagi Traffic`);
                  }}
                  data-testid="send-custom-alert"
                >
                  <MessageCircle className="w-3 h-3" /> Share Custom Alert
                </Button>
              </div>

              {/* WhatsApp Channel */}
              <div className="p-6 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                <MessageCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Join Our WhatsApp Channel</h3>
                <p className="text-sm text-muted-foreground mb-4">Get daily traffic updates for Kalaburagi directly on WhatsApp</p>
                <a href="https://wa.me/919876543210?text=Subscribe%20to%20Kalaburagi%20Traffic%20Alerts" target="_blank" rel="noopener noreferrer">
                  <Button className="gap-2 bg-green-600 hover:bg-green-700" data-testid="join-whatsapp-channel">
                    <MessageCircle className="w-4 h-4" /> Join Channel
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Traffic Planning */}
      {activeTab === 'events' && (
        <div className="space-y-6 animate-fade-in" data-testid="events-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarPlus className="w-5 h-5 text-primary" />
                Event Traffic Planning
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Plan ahead for events, festivals, and road closures that affect traffic in Kalaburagi.
              </p>

              {/* Add Event (Admin only) */}
              {user?.role === 'admin' && (
                <div className="p-4 rounded-xl border bg-muted/30 space-y-3">
                  <h4 className="font-bold text-sm">Add Traffic Event</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Event Name *</Label>
                      <Input
                        placeholder="e.g. Ganesh Festival Procession"
                        value={newEvent.name}
                        onChange={e => setNewEvent({ ...newEvent, name: e.target.value })}
                        data-testid="event-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Affected Area *</Label>
                      <Select value={newEvent.area} onValueChange={v => setNewEvent({ ...newEvent, area: v })}>
                        <SelectTrigger data-testid="event-area-select">
                          <SelectValue placeholder="Select area" />
                        </SelectTrigger>
                        <SelectContent>
                          {AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={newEvent.date}
                        onChange={e => setNewEvent({ ...newEvent, date: e.target.value })}
                        data-testid="event-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Impact Level</Label>
                      <Select value={newEvent.impact} onValueChange={v => setNewEvent({ ...newEvent, impact: v })}>
                        <SelectTrigger data-testid="event-impact-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Minor delay</SelectItem>
                          <SelectItem value="medium">Medium - Significant delay</SelectItem>
                          <SelectItem value="high">High - Road closure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Details about the event and traffic impact..."
                      value={newEvent.description}
                      onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                      rows={2}
                      data-testid="event-description-input"
                    />
                  </div>
                  <Button onClick={addEvent} disabled={addingEvent} className="gap-2" data-testid="add-event-btn">
                    {addingEvent ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                    Add Event
                  </Button>
                </div>
              )}

              {/* Events List */}
              <div className="space-y-3">
                {events.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarPlus className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No upcoming traffic events</p>
                  </div>
                ) : (
                  events.map((event, i) => (
                    <div key={event.id} className="p-4 rounded-xl border hover:shadow-md transition-shadow animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`event-${event.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-sm">{event.name}</h4>
                            <Badge className={`text-xs ${
                              event.impact === 'high' ? 'bg-red-500 text-white' :
                              event.impact === 'medium' ? 'bg-yellow-500 text-black' : 'bg-green-500 text-white'
                            }`}>
                              {event.impact}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.area}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.date}</span>
                          </div>
                          {event.description && <p className="text-sm text-muted-foreground mt-2">{event.description}</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => shareOnWhatsApp(
                              `📅 Kalaburagi Traffic Event:\n${event.name}\n📍 ${event.area}\n📆 ${event.date}\nImpact: ${event.impact}\n${event.description || ''}\n\nPlan your route: ${window.location.origin}/smart`
                            )}
                          >
                            <MessageCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          {user?.role === 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => deleteEvent(event.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
